import {
  FileSignalIcon as AudioIcon,
  BoxIcon,
  FolderArchiveIcon as CompressedFilesIcon,
  Table2Icon as DatabaseIcon,
  FileIcon as DocumentIcon,
  FileIcon as FolderOpenIcon,
  ImageIcon,
  FileTextIcon as PdfIcon,
  ChartPieIcon as PresentationsIcon,
  PenToolIcon as VectorIcon,
  FileVideoCameraIcon as VideoIcon,
} from '@air/design-icons';

export type IconType = typeof ImageIcon;

export type FileGroupType = {
  iconName: string;
  iconComponent: IconType;
  iconColor: string;
  bgColor: string;
  modalIconColor: string;
};

const audioFile: FileGroupType = {
  bgColor: '#EDE6FF',
  iconColor: '#010256',
  iconComponent: AudioIcon,
  iconName: 'Audio',
  modalIconColor: '#AC78FF',
};

const compressedFile: FileGroupType = {
  bgColor: '#FDD778',
  iconColor: '#4C2103',
  iconComponent: CompressedFilesIcon,
  iconName: 'Compressed files',
  modalIconColor: '#CE4D13',
};

const databaseFile: FileGroupType = {
  bgColor: '#AAF255',
  iconColor: '#033B51',
  iconComponent: DatabaseIcon,
  iconName: 'Database',
  modalIconColor: '#61D800',
};

export const genericFile: FileGroupType = {
  bgColor: '#DCDCDC',
  iconColor: '#4D4D4D',
  iconComponent: FolderOpenIcon,
  iconName: 'Folder open',
  modalIconColor: '#666666',
};

const imageFile: FileGroupType = {
  bgColor: '#E6F4F6',
  iconColor: '#07B3BE',
  iconComponent: ImageIcon,
  iconName: 'Image',
  modalIconColor: '#05889F',
};

const pdfFile: FileGroupType = {
  bgColor: '#FCF0F0',
  iconColor: '#ED3B3B',
  iconComponent: PdfIcon,
  iconName: 'PDF',
  modalIconColor: '#ED3B3B',
};

const presentationFile: FileGroupType = {
  bgColor: '#FFAA3B',
  iconColor: '#4C2103',
  iconComponent: PresentationsIcon,
  iconName: 'Presentations',
  modalIconColor: '#FF6D00',
};

const textFile: FileGroupType = {
  bgColor: '#91AEFF',
  iconColor: '#0204A4',
  iconComponent: DocumentIcon,
  iconName: 'Document',
  modalIconColor: '#3820EA',
};

const threeDFile: FileGroupType = {
  bgColor: '#B4CFFF',
  iconColor: '#280DBC',
  iconComponent: BoxIcon,
  iconName: '3D',
  modalIconColor: '#280DBC',
};

const vectorFile: FileGroupType = {
  bgColor: '#70E7E7',
  iconColor: '#05889F',
  iconComponent: VectorIcon,
  iconName: 'Vector',
  modalIconColor: '#033B51',
};

const videoFile: FileGroupType = {
  bgColor: '#ff8383',
  iconColor: '#841328',
  iconComponent: VideoIcon,
  iconName: 'Video',
  modalIconColor: '#a1222f',
};

export type FileExtensionName =
  | '3ds'
  | '3fr'
  | 'ai'
  | 'ari'
  | 'aep'
  | 'aiff'
  | 'ark'
  | 'arw'
  | 'avi'
  | 'bay'
  | 'bitmap'
  | 'blend'
  | 'bmp'
  | 'bz'
  | 'c4d'
  | 'cap'
  | 'collada'
  | 'cr2'
  | 'crw'
  | 'csv'
  | 'dat'
  | 'data'
  | 'db'
  | 'dcr'
  | 'dcs'
  | 'dng'
  | 'drf'
  | 'dbf'
  | 'doc'
  | 'docx'
  | 'dwg'
  | 'eip'
  | 'erf'
  | 'eps'
  | 'exif'
  | 'faw'
  | 'fbx'
  | 'fcp'
  | 'fff'
  | 'fla'
  | 'flac'
  | 'flv'
  | 'gif'
  | 'gpr'
  | 'hdri'
  | 'html'
  | 'iges'
  | 'iiq'
  | 'indb'
  | 'indd'
  | 'jpg'
  | 'jpeg'
  | 'k25'
  | 'kdc'
  | 'key'
  | 'log'
  | 'm4a'
  | 'ma'
  | 'mb'
  | 'mdb'
  | 'mdc'
  | 'mef'
  | 'mel'
  | 'midi'
  | 'mos'
  | 'MOV'
  | 'mov'
  | 'mrw'
  | 'mp3'
  | 'mp4'
  | 'mpg'
  | 'nef'
  | 'nrw'
  | 'numbers'
  | 'obm'
  | 'odp'
  | 'ods'
  | 'odt'
  | 'obj'
  | 'orf'
  | 'pages'
  | 'pef'
  | 'pdf'
  | 'png'
  | 'ppt'
  | 'pptx'
  | 'prproj'
  | 'psb'
  | 'psd'
  | 'ptx'
  | 'pxn'
  | 'raf'
  | 'rar'
  | 'raw'
  | 'rtf'
  | 'rw2'
  | 'rwl'
  | 'rwz'
  | 'sav'
  | 'sr2'
  | 'srf'
  | 'srw'
  | 'sql'
  | 'step'
  | 'stl'
  | 'svg'
  | 'tar'
  | 'tif'
  | 'tiff'
  | 'txt'
  | 'vrml'
  | 'wav'
  | 'webp'
  | 'wma'
  | 'wmv'
  | 'x3d'
  | 'x3f'
  | 'xls'
  | 'xlsx'
  | 'xml'
  | 'zip';

export const fileExtensionStylingMap: { [key in FileExtensionName]: FileGroupType } = {
  '3ds': threeDFile,
  '3fr': imageFile,
  aep: genericFile,
  ai: vectorFile,
  aiff: audioFile,
  ari: imageFile,
  ark: compressedFile,
  arw: imageFile,
  avi: videoFile,
  bay: imageFile,
  bitmap: imageFile,
  blend: threeDFile,
  bmp: imageFile,
  bz: compressedFile,
  c4d: threeDFile,
  cap: imageFile,
  collada: threeDFile,
  cr2: imageFile,
  crw: imageFile,
  csv: databaseFile,
  dat: databaseFile,
  data: imageFile,
  db: databaseFile,
  dbf: databaseFile,
  dcr: imageFile,
  dcs: imageFile,
  dng: imageFile,
  doc: textFile,
  docx: textFile,
  drf: imageFile,
  dwg: threeDFile,
  eip: imageFile,
  eps: vectorFile,
  erf: imageFile,
  exif: imageFile,
  faw: imageFile,
  fbx: threeDFile,
  fcp: genericFile,
  fff: imageFile,
  fla: genericFile,
  flac: audioFile,
  flv: videoFile,
  gif: imageFile,
  gpr: imageFile,
  hdri: imageFile,
  html: textFile,
  iges: threeDFile,
  iiq: imageFile,
  indb: genericFile,
  indd: genericFile,
  jpeg: imageFile,
  jpg: imageFile,
  k25: imageFile,
  kdc: imageFile,
  key: presentationFile,
  log: textFile,
  MOV: videoFile,
  m4a: audioFile,
  ma: threeDFile,
  mb: threeDFile,
  mdb: databaseFile,
  mdc: imageFile,
  mef: imageFile,
  mel: threeDFile,
  midi: audioFile,
  mos: imageFile,
  mov: videoFile,
  mp3: audioFile,
  mp4: videoFile,
  mpg: videoFile,
  mrw: imageFile,
  nef: imageFile,
  nrw: imageFile,
  numbers: databaseFile,
  obj: threeDFile,
  obm: imageFile,
  odp: presentationFile,
  ods: databaseFile,
  odt: textFile,
  orf: imageFile,
  pages: textFile,
  pdf: pdfFile,
  pef: imageFile,
  png: imageFile,
  ppt: presentationFile,
  pptx: presentationFile,
  prproj: genericFile,
  psb: imageFile,
  psd: imageFile,
  ptx: imageFile,
  pxn: imageFile,
  raf: imageFile,
  rar: compressedFile,
  raw: imageFile,
  rtf: textFile,
  rw2: imageFile,
  rwl: imageFile,
  rwz: imageFile,
  sav: databaseFile,
  sql: databaseFile,
  sr2: imageFile,
  srf: imageFile,
  srw: imageFile,
  step: threeDFile,
  stl: threeDFile,
  svg: vectorFile,
  tar: databaseFile,
  tif: imageFile,
  tiff: imageFile,
  txt: textFile,
  vrml: threeDFile,
  wav: audioFile,
  webp: imageFile,
  wma: audioFile,
  wmv: videoFile,
  x3d: threeDFile,
  x3f: imageFile,
  xls: databaseFile,
  xlsx: databaseFile,
  xml: databaseFile,
  zip: compressedFile,
};

export enum ClipIconSize {
  small = 20,
  mediumSmall = 24,
  medium = 36,
  large = 48,
}
